/**
 * v2 API 정의 모듈
 *
 * 잔류물질안전정보시스템 v3의 전체 API 엔드포인트를 정의한다.
 * 모든 요청은 v2Client (baseURL: /api/v2)를 통해 수행된다.
 *
 * 목차:
 *  1. prdApi       - 농약 관리 (잔류허용기준, 식품, ADI, 사용현황, 분석법, 보고서)
 *  2. proposalApi  - 기준안 관리
 *  3. vdApi        - 동물용의약품 관리
 *  4. stdApi       - 시험법 + 신청 + 업체/현황/단위 + 의뢰
 *  5. docApi       - 게시물 + 관련자료
 *  6. homeApi      - 홈화면 (검색, 통계, 팝업)
 *  7. searchApi    - 통합검색
 *  8. adminApi     - 관리자 (대시보드, 사용자, 권한템플릿, 방문자, 감사, 워크플로우 등)
 *  9. commonApi    - 공통코드
 */
import axios from 'axios';
import v2Client from './v2Client';

/**
 * 인증 관련 요청 전용 axios 인스턴스
 * — 백엔드 auth 라우트는 `/auth/*`에 마운트되어 있어 v2Client(baseURL: /api/v2)를 사용할 수 없음.
 * — withCredentials: true 로 세션 쿠키 전송 보장
 */
const authHttp = axios.create({
  withCredentials: true,
  timeout: 30000,
});

// ═══════════════════════════════════════════════════════════
// 1. prdApi - 농약(Pesticides) 관리
// ═══════════════════════════════════════════════════════════

export const prdApi = {
  // ─── 농약 기본 CRUD ───────────────────────────────────────

  /** 농약 목록 조회 */
  getList: (params) => v2Client.get('/prd/pesticides', { params }),

  /** 농약 상세 조회 */
  getDetail: (code) => v2Client.get(`/prd/pesticides/${code}`),

  /** 농약 전체 엑셀 다운로드용 일괄 조회 (단일 SQL) */
  getExcelBulk: () => v2Client.get('/prd/pesticides/export/excel'),

  /** 농약 등록 */
  create: (data) => v2Client.post('/prd/pesticides', data),

  /** 농약 수정 */
  update: (code, data) => v2Client.put(`/prd/pesticides/${code}`, data),

  /** 농약 삭제 */
  remove: (code) => v2Client.delete(`/prd/pesticides/${code}`),

  // ─── 잔류허용기준(MRL) ────────────────────────────────────

  /** MRL 목록 조회 */
  getMrls: (params) => v2Client.get('/prd/mrls', { params }),

  /** MRL 등록 */
  createMrl: (data) => v2Client.post('/prd/mrls', data),

  /** MRL 수정 (복합키: pesticideCode/foodCode/countryCode) */
  updateMrl: (pesticideCode, foodCode, countryCode, data) =>
    v2Client.put(`/prd/mrls/${pesticideCode}/${foodCode}/${countryCode}`, data),

  /** MRL 삭제 (복합키) */
  removeMrl: (pesticideCode, foodCode, countryCode) =>
    v2Client.delete(`/prd/mrls/${pesticideCode}/${foodCode}/${countryCode}`),

  /** MRL 일괄 수정 (DISCARD + INSERT) */
  batchUpdateMrl: (data) => v2Client.put('/prd/mrls/batch-update', data),

  /** MRL 일괄 교체 (엑셀 기반 — 농약 단위 전체 교체, 트랜잭션) */
  // 대량 반영은 기본 30초 타임아웃으로 부족 — 180초 오버라이드
  bulkReplaceMrl: (items, discardMode) => v2Client.put('/prd/mrls/bulk-replace', { items, discardMode }, { timeout: 180000 }),

  /** MRL 일괄 폐기 */
  batchDiscardMrl: (data) => v2Client.put('/prd/mrls/batch-discard', data),

  /** MRL 건수 조회 */
  getMrlCount: (params) => v2Client.get('/prd/mrls/count', { params }),

  /** MRL 추가정보 전체 조회 */
  getMrlAdditionalInfo: () => v2Client.get('/prd/mrls/additional-info'),

  /** MRL 추가정보 전체 교체 */
  replaceMrlAdditionalInfo: (data) => v2Client.put('/prd/mrls/additional-info', data),

  // ─── 식품(Foods) ──────────────────────────────────────────

  /** 식품 목록 조회 */
  getFoods: (params) => v2Client.get('/prd/foods', { params }),

  /** 식품 상세 조회 */
  getFoodDetail: (code) => v2Client.get(`/prd/foods/${code}`),

  /** 식품 분류 조회 */
  getFoodClassification: ({ type, ...rest }) => v2Client.get(`/prd/foods/classification/${type}`, { params: rest }),

  /** 식품 등록 */
  createFood: (data) => v2Client.post('/prd/foods', data),

  /** 식품 수정 */
  updateFood: (code, data) => v2Client.put(`/prd/foods/${code}`, data),

  /** 식품 삭제 */
  removeFood: (code) => v2Client.delete(`/prd/foods/${code}`),

  // ─── ADI (일일섭취허용량) ──────────────────────────────────

  /** ADI 목록 조회 */
  getAdiList: (params) => v2Client.get('/prd/adi', { params }),

  /** ADI 상세 조회 */
  getAdiDetail: (pesticideCode) => v2Client.get(`/prd/adi/${pesticideCode}`),

  /** ADI 대체(전체 교체) */
  replaceAdi: (pesticideCode, data) => v2Client.put(`/prd/adi/${pesticideCode}`, data),

  // ─── 사용현황(Using) ──────────────────────────────────────

  /** 사용현황 목록 조회 */
  getUsingList: (params) => v2Client.get('/prd/using', { params }),

  /** 사용현황 상세 조회 */
  getUsingDetail: (id) => v2Client.get(`/prd/using/${id}`),

  /** 사용현황 등록 */
  createUsing: (data) => v2Client.post('/prd/using', data),

  /** 사용현황 수정 */
  updateUsing: (id, data) => v2Client.put(`/prd/using/${id}`, data),

  /** 사용현황 삭제 */
  removeUsing: (id) => v2Client.delete(`/prd/using/${id}`),

  // ─── 분석법(Analysis) ─────────────────────────────────────

  /** 분석법 목록 조회 */
  getAnalysisList: (params) => v2Client.get('/prd/analysis', { params }),

  /** 분석법 코드별 조회 */
  getAnalysisByCode: (code) => v2Client.get(`/prd/analysis/by-code/${code}`),

  /** 분석법 상세 조회 */
  getAnalysisDetail: (id) => v2Client.get(`/prd/analysis/${id}`),

  /** 분석법 등록 */
  createAnalysis: (data) => v2Client.post('/prd/analysis', data),

  /** 분석법 수정 */
  updateAnalysis: (id, data) => v2Client.put(`/prd/analysis/${id}`, data),

  /** 분석법 삭제 */
  removeAnalysis: (id) => v2Client.delete(`/prd/analysis/${id}`),

  // ─── 분석법-L(Analysis-L) ─────────────────────────────────

  /** 분석법-L 목록 조회 */
  getAnalysisLList: (params) => v2Client.get('/prd/analysis-l', { params }),

  /** 분석법-L 코드별 조회 */
  getAnalysisLByCode: (code) => v2Client.get(`/prd/analysis-l/by-code/${code}`),

  /** 분석법-L 상세 조회 */
  getAnalysisLDetail: (id) => v2Client.get(`/prd/analysis-l/${id}`),

  /** 분석법-L 등록 */
  createAnalysisL: (data) => v2Client.post('/prd/analysis-l', data),

  /** 분석법-L 수정 */
  updateAnalysisL: (id, data) => v2Client.put(`/prd/analysis-l/${id}`, data),

  /** 분석법-L 삭제 */
  removeAnalysisL: (id) => v2Client.delete(`/prd/analysis-l/${id}`),

  // ─── 보고서(Reports) ──────────────────────────────────────

  /** 보고서 목록 조회 */
  getReportList: (params) => v2Client.get('/prd/reports', { params }),

  /** 보고서 상세 조회 */
  getReportDetail: (id) => v2Client.get(`/prd/reports/${id}`),

  /** 보고서 등록 */
  createReport: (data) => v2Client.post('/prd/reports', data),

  /** 보고서 수정 */
  updateReport: (id, data) => v2Client.put(`/prd/reports/${id}`, data),

  /** 보고서 삭제 */
  removeReport: (id) => v2Client.delete(`/prd/reports/${id}`),

  /** 보고서 확정 */
  confirmReport: (id) => v2Client.put(`/prd/reports/${id}/confirm`),

  /** 보고서 공개 토글 */
  toggleOpenReport: (id) => v2Client.put(`/prd/reports/${id}/toggle-open`),

  // ─── 안전성 보고서 업로드 ─────────────────────────────────

  /** 안전성 보고서 파일 업로드 (multipart/form-data) */
  uploadSafetyReport: (code, formData) =>
    v2Client.post(`/prd/pesticides/${code}/safety-report`, formData),

  /** 안전성 보고서 파일 삭제 */
  deleteSafetyReport: (code) =>
    v2Client.delete(`/prd/pesticides/${code}/safety-report`),

  /** 농약 관계 동기화 (stdNames, diffNames, productNames, usingNums) */
  syncRelations: (code, data) =>
    v2Client.put(`/prd/pesticides/${code}/relations`, data),
};

// ═══════════════════════════════════════════════════════════
// 2. proposalApi - 기준안(Proposals) 관리
// ═══════════════════════════════════════════════════════════

export const proposalApi = {
  // ─── 기준안 기본 CRUD ─────────────────────────────────────

  /** 기준안 목록 조회 */
  getList: (params) => v2Client.get('/prd/proposals', { params }),

  /** 기준안 상세 조회 */
  getDetail: (id) => v2Client.get(`/prd/proposals/${id}`),

  /** 기준안 등록 */
  create: (data) => v2Client.post('/prd/proposals', data),

  /** 기준안 수정 */
  update: (id, data) => v2Client.put(`/prd/proposals/${id}`, data),

  /** 기준안 삭제 */
  remove: (id) => v2Client.delete(`/prd/proposals/${id}`),

  // ─── 기준안 MRL 관계 ──────────────────────────────────────

  /** 기준안 MRL 관계 조회 */
  getMrlRelations: (id, params) =>
    v2Client.get(`/prd/proposals/${id}/mrls`, { params }),

  /** 기준안 MRL 관계 목록 조회 */
  getMrlRelationsList: (id, params) =>
    v2Client.get(`/prd/proposals/${id}/mrls/list`, { params }),

  /** 기준안 MRL 관계 등록 */
  createMrlRelation: (id, data) =>
    v2Client.post(`/prd/proposals/${id}/mrls`, data),

  /** 기준안 MRL 관계 수정 */
  updateMrlRelation: (id, mrlId, data) =>
    v2Client.put(`/prd/proposals/${id}/mrls/${mrlId}`, data),

  /** 기준안 MRL 관계 삭제 */
  deleteMrlRelation: (id, mrlId) =>
    v2Client.delete(`/prd/proposals/${id}/mrls/${mrlId}`),

  /** 기준안 MRL 관계 전체 삭제 */
  deleteAllMrlRelations: (id) =>
    v2Client.delete(`/prd/proposals/${id}/mrls`),

  /** 현행 한국 MRL 조회 */
  getCurrentKrMrl: (id, params) =>
    v2Client.get(`/prd/proposals/${id}/mrls/current-kr`, { params }),

  // ─── 기준안 국가 관계 ─────────────────────────────────────

  /** MRL별 국가 관계 조회 */
  getNationRelations: (mrlId) =>
    v2Client.get(`/prd/proposals/mrls/${mrlId}/nations`),

  /** MRL별 국가 관계 일괄 저장 */
  batchSaveNationRelations: (mrlId, data) =>
    v2Client.post(`/prd/proposals/mrls/${mrlId}/nations`, data),

  // ─── 기준안 단계 관계 ─────────────────────────────────────

  /** MRL별 단계 관계 조회 */
  getStepRelations: (mrlId) =>
    v2Client.get(`/prd/proposals/mrls/${mrlId}/steps`),

  /** MRL별 단계 관계 일괄 저장 */
  batchSaveStepRelations: (mrlId, data) =>
    v2Client.post(`/prd/proposals/mrls/${mrlId}/steps`, data),

  // ─── 기준안 작물 관계 ─────────────────────────────────────

  /** 기준안별 작물 관계 조회 */
  getCropsRelations: (id, params) =>
    v2Client.get(`/prd/proposals/${id}/crops`, { params }),

  /** 기준안별 작물 관계 일괄 저장 */
  batchSaveCropsRelations: (id, data) =>
    v2Client.post(`/prd/proposals/${id}/crops`, data),
};

// ═══════════════════════════════════════════════════════════
// 3. vdApi - 동물용의약품(VD Drugs) 관리
// ═══════════════════════════════════════════════════════════

export const vdApi = {
  // ─── 동물용의약품 기본 CRUD ───────────────────────────────

  /** 동물용의약품 목록 조회 */
  getList: (params) => v2Client.get('/vd/drugs', { params }),

  /** 동물용의약품 상세 조회 */
  getDetail: (code) => v2Client.get(`/vd/drugs/${code}`),

  /** 동물용의약품 전체 엑셀 다운로드용 일괄 조회 */
  getExcelBulk: () => v2Client.get('/vd/drugs/export/excel'),

  /** 동물용의약품 등록 */
  create: (data) => v2Client.post('/vd/drugs', data),

  /** 동물용의약품 수정 */
  update: (code, data) => v2Client.put(`/vd/drugs/${code}`, data),

  /** 동물용의약품 삭제 */
  remove: (code) => v2Client.delete(`/vd/drugs/${code}`),

  /** 동물용의약품 관계 동기화 */
  syncRelations: (code, data) => v2Client.put(`/vd/drugs/${code}/relations`, data),

  // ─── 잔류허용기준(MRL) ────────────────────────────────────

  /** VD MRL 목록 조회 */
  getMrls: (params) => v2Client.get('/vd/mrls', { params }),

  /** VD MRL 등록 */
  createMrl: (data) => v2Client.post('/vd/mrls', data),

  /** VD MRL 수정 (복합키: vdrugCode/foodCode/countryCode) */
  updateMrl: (vdrugCode, foodCode, countryCode, data) =>
    v2Client.put(`/vd/mrls/${vdrugCode}/${foodCode}/${countryCode}`, data),

  /** VD MRL 삭제 (복합키) */
  removeMrl: (vdrugCode, foodCode, countryCode) =>
    v2Client.delete(`/vd/mrls/${vdrugCode}/${foodCode}/${countryCode}`),

  /** VD MRL 일괄 수정 */
  batchUpdateMrl: (data) => v2Client.put('/vd/mrls/batch-update', data),

  /** VD MRL 일괄 폐기 */
  batchDiscardMrl: (data) => v2Client.put('/vd/mrls/batch-discard', data),

  /** VD MRL 일괄 교체 (엑셀 기반, 트랜잭션) */
  // 대량 반영은 기본 30초 타임아웃으로 부족 — 180초 오버라이드
  bulkReplaceMrl: (items, discardMode) => v2Client.put('/vd/mrls/bulk-replace', { items, discardMode }, { timeout: 180000 }),

  /** VD MRL 건수 조회 */
  getMrlCount: (params) => v2Client.get('/vd/mrls/count', { params }),

  /** VD MRL 부가정보 조회 */
  getMrlAdditionalInfo: () => v2Client.get('/vd/mrls/additional-info'),

  /** VD MRL 부가정보 대체 */
  replaceMrlAdditionalInfo: (data) => v2Client.put('/vd/mrls/additional-info', data),

  /** VD MRL 부가정보2 조회 */
  getMrlAdditionalInfo2: () => v2Client.get('/vd/mrls/additional-info2'),

  /** VD MRL 부가정보2 대체 */
  replaceMrlAdditionalInfo2: (data) => v2Client.put('/vd/mrls/additional-info2', data),

  // ─── 식품(Foods) ──────────────────────────────────────────

  /** VD 식품 목록 조회 */
  getFoods: (params) => v2Client.get('/vd/foods', { params }),

  /** VD 식품 상세 조회 */
  getFoodDetail: (code) => v2Client.get(`/vd/foods/${code}`),

  /** VD 식품 분류 조회 */
  getFoodClassification: ({ type, ...rest }) => v2Client.get(`/vd/foods/classification/${type}`, { params: rest }),

  /** VD 식품 등록 */
  createFood: (data) => v2Client.post('/vd/foods', data),

  /** VD 식품 수정 */
  updateFood: (code, data) => v2Client.put(`/vd/foods/${code}`, data),

  /** VD 식품 삭제 */
  removeFood: (code) => v2Client.delete(`/vd/foods/${code}`),

  // ─── ADI (일일섭취허용량) ──────────────────────────────────

  /** VD ADI 목록 조회 */
  getAdiList: (params) => v2Client.get('/vd/adi', { params }),

  /** VD ADI 상세 조회 */
  getAdiDetail: (vdrugCode) => v2Client.get(`/vd/adi/${vdrugCode}`),

  /** VD ADI 대체 */
  replaceAdi: (vdrugCode, data) => v2Client.put(`/vd/adi/${vdrugCode}`, data),

  // ─── 사용현황(Using) ──────────────────────────────────────

  /** VD 사용현황 목록 조회 */
  getUsingList: (params) => v2Client.get('/vd/using', { params }),

  /** VD 사용현황 상세 조회 */
  getUsingDetail: (id) => v2Client.get(`/vd/using/${id}`),

  /** VD 사용현황 등록 */
  createUsing: (data) => v2Client.post('/vd/using', data),

  /** VD 사용현황 수정 */
  updateUsing: (id, data) => v2Client.put(`/vd/using/${id}`, data),

  /** VD 사용현황 삭제 */
  removeUsing: (id) => v2Client.delete(`/vd/using/${id}`),

  // ─── 분석법(Analysis) ─────────────────────────────────────

  /** VD 분석법 목록 조회 */
  getAnalysisList: (params) => v2Client.get('/vd/analysis', { params }),

  /** VD 분석법 코드별 조회 */
  getAnalysisByCode: (code) => v2Client.get(`/vd/analysis/by-code/${code}`),

  /** VD 분석법 상세 조회 */
  getAnalysisDetail: (id) => v2Client.get(`/vd/analysis/${id}`),

  /** VD 분석법 등록 */
  createAnalysis: (data) => v2Client.post('/vd/analysis', data),

  /** VD 분석법 수정 */
  updateAnalysis: (id, data) => v2Client.put(`/vd/analysis/${id}`, data),

  /** VD 분석법 삭제 */
  removeAnalysis: (id) => v2Client.delete(`/vd/analysis/${id}`),
};

// ═══════════════════════════════════════════════════════════
// 4. stdApi - 시험법(Standards) + 신청 + 업체/현황/단위 + 의뢰
// ═══════════════════════════════════════════════════════════

export const stdApi = {
  // ─── 시험법 기본 CRUD ─────────────────────────────────────

  /** 시험법 목록 조회 */
  getList: (params) => v2Client.get('/std/standards', { params }),

  /** 시험법 상세 조회 */
  getDetail: (id) => v2Client.get(`/std/standards/${id}`),

  /** 시험법 등록 */
  create: (data) => v2Client.post('/std/standards', data),

  // 일괄 등록 (트랜잭션 — 전체 성공 또는 전체 거부). 대량 처리라 타임아웃 180초
  bulkCreate: (items) => v2Client.post('/std/standards/bulk', { items }, { timeout: 180000 }),

  /** 시험법 수정 */
  update: (id, data) => v2Client.put(`/std/standards/${id}`, data),

  /** 시험법 삭제 */
  remove: (id) => v2Client.delete(`/std/standards/${id}`),

  // 일괄 삭제 사전 검증 (dry-run — 삭제 없음)
  bulkRemovePreview: (ids) => v2Client.post('/std/standards/bulk-delete/preview', { ids }),

  // 일괄 삭제 (전체 성공 또는 전체 거부)
  bulkRemove: (ids) => v2Client.post('/std/standards/bulk-delete', { ids }),

  /** 시험법 구분 목록 조회 */
  getStandardDivisions: (id) => v2Client.get(`/std/standards/${id}/divisions`),

  /** 시약명 목록 조회 */
  getReagentNames: () => v2Client.get('/std/standards/reagent-names'),

  /** 시험법명 최대 번호 조회 */
  getMaxStdNameNum: (params) => v2Client.get('/std/standards/max-name-num', { params }),

  // ─── 시약신청 (Applications, v3 재설계) ─────────────────
  // 상태 머신 기반 엔드포인트:
  //   PENDING → SUPPLIED → RECEIVED
  //      ↓
  //   REJECTED_SOLD_OUT / REJECTED_OTHER
  //      ↓
  //   CANCELLED

  /** 신청 목록 조회 (일반회원은 본인만, 관리자는 전체) */
  getApplications: (params) => v2Client.get('/std/applications', { params }),

  /** 신청 상세 조회 */
  getApplicationDetail: (id) => v2Client.get(`/std/applications/${id}`),

  /** 엑셀 내보내기 전체 데이터 */
  getApplicationExportData: () => v2Client.get('/std/applications/export'),

  /** 선택 가능한 시약 목록 (재고/만료 필터, includeAll로 토글) */
  getAvailableStandards: (params) =>
    v2Client.get('/std/applications/standards', { params }),

  /** 시도 목록 */
  getApplicationProvinces: () => v2Client.get('/std/applications/provinces'),

  /** 본인 프로필 (자동 채움용) */
  getMyApplicationProfile: () => v2Client.get('/std/applications/me/profile'),

  /** Rate limit 상태 (최근 30일 본인 건수 + 제한값) */
  getMyRateLimit: () => v2Client.get('/std/applications/me/rate-limit'),

  /** 배지용 조치 필요 건수 */
  getPendingActionCount: () => v2Client.get('/std/applications/me/pending-count'),

  /** 신청 생성 */
  createApplication: (data) => v2Client.post('/std/applications', data),

  /** 본인 기본정보 수정 (PENDING 상태만) */
  updateApplication: (id, data) => v2Client.put(`/std/applications/${id}`, data),

  /** 관리자 지급 처리 */
  processSupply: (id, data) =>
    v2Client.post(`/std/applications/${id}/supply`, data),

  /** 관리자 반려 */
  rejectApplication: (id, data) =>
    v2Client.post(`/std/applications/${id}/reject`, data),

  /** 본인 수령 확인/취소 토글 (확인 시 receiveName 선택적 입력) */
  toggleReceive: (id, confirm = true, receiveName) =>
    v2Client.post(`/std/applications/${id}/receive`, { confirm, receiveName }),

  /** 신청 취소 */
  cancelApplication: (id) =>
    v2Client.post(`/std/applications/${id}/cancel`),

  /** 재신청 (REJECTED 상태에서 복제) */
  cloneApplication: (id) =>
    v2Client.post(`/std/applications/${id}/clone`),

  /** 관리자 일괄 임포트 */
  bulkImportApplication: (items) =>
    v2Client.post('/std/applications/bulk', { items }),

  /** 관리자 일괄 업로드 검증 */
  validateBulkImportApplication: (rows) =>
    v2Client.post('/std/applications/bulk/validate', { rows }),

  /** 관리자 일괄 업로드 등록 */
  commitBulkImportApplication: (rows) =>
    v2Client.post('/std/applications/bulk/commit', { rows }),

  /** @deprecated 레거시 호환 - DELETE 대신 cancelApplication 사용 권장 */
  removeApplication: (id) => v2Client.delete(`/std/applications/${id}`),

  // ─── 업체(Companies) ──────────────────────────────────────

  /** 업체 목록 조회 */
  getCompanies: (params) => v2Client.get('/std/companies', { params }),

  /** 업체 상세 조회 */
  getCompanyDetail: (id) => v2Client.get(`/std/companies/${id}`),

  /** 업체 등록 */
  createCompany: (data) => v2Client.post('/std/companies', data),

  /** 업체 수정 */
  updateCompany: (id, data) => v2Client.put(`/std/companies/${id}`, data),

  /** 업체 삭제 */
  removeCompany: (id) => v2Client.delete(`/std/companies/${id}`),

  // ─── 현황(Statuses) ───────────────────────────────────────

  /** 현황 목록 조회 */
  getStatuses: (params) => v2Client.get('/std/statuses', { params }),

  /** 현황 상세 조회 */
  getStatusDetail: (id) => v2Client.get(`/std/statuses/${id}`),

  /** 현황 등록 */
  createStatus: (data) => v2Client.post('/std/statuses', data),

  /** 현황 수정 */
  updateStatus: (id, data) => v2Client.put(`/std/statuses/${id}`, data),

  /** 현황 삭제 */
  removeStatus: (id) => v2Client.delete(`/std/statuses/${id}`),

  // ─── 단위(Units) ──────────────────────────────────────────

  /** 단위 목록 조회 */
  getUnits: (params) => v2Client.get('/std/units', { params }),

  /** 단위 상세 조회 */
  getUnitDetail: (id) => v2Client.get(`/std/units/${id}`),

  /** 단위 등록 */
  createUnit: (data) => v2Client.post('/std/units', data),

  /** 단위 수정 */
  updateUnit: (id, data) => v2Client.put(`/std/units/${id}`, data),

  /** 단위 삭제 */
  removeUnit: (id) => v2Client.delete(`/std/units/${id}`),

  // ─── 의뢰(Requests) ──────────────────────────────────────

  /** 의뢰 목록 조회 */
  getRequestList: (params) => v2Client.get('/std/requests', { params }),

  /** 의뢰 상세 조회 */
  getRequestDetail: (id) => v2Client.get(`/std/requests/${id}`),

  /** 의뢰 사용자 시도 조회 */
  getRequestUserProvince: () => v2Client.get('/std/requests/user-province'),

  /** 의뢰 등록 */
  createRequest: (data) => v2Client.post('/std/requests', data),

  /** 의뢰 수정 */
  updateRequest: (id, data) => v2Client.put(`/std/requests/${id}`, data),

  /** 의뢰 삭제 */
  removeRequest: (id) => v2Client.delete(`/std/requests/${id}`),
};

// ═══════════════════════════════════════════════════════════
// 5. docApi - 게시물(Articles) + 관련자료
// ═══════════════════════════════════════════════════════════

export const docApi = {
  // ─── 게시물 기본 CRUD ─────────────────────────────────────

  /** 게시물 목록 조회 */
  getList: (params) => v2Client.get('/doc/articles', { params }),

  /** 게시물 상세 조회 */
  getDetail: (id) => v2Client.get(`/doc/articles/${id}`),

  /** 게시물 등록 */
  create: (data) => v2Client.post('/doc/articles', data),

  /** 게시물 수정 */
  update: (id, data) => v2Client.put(`/doc/articles/${id}`, data),

  /** 게시물 삭제 */
  remove: (id) => v2Client.delete(`/doc/articles/${id}`),

  // ─── 파일 관리 ────────────────────────────────────────────

  /** 파일 업로드 */
  uploadFile: (id, formData) =>
    v2Client.post(`/doc/articles/${id}/file`, formData),

  /** 파일 다운로드 (blob) */
  downloadFile: (id) =>
    v2Client.get(`/doc/articles/${id}/file`, {
      responseType: 'blob',
    }),

  /** 파일 삭제 */
  deleteFile: (id) =>
    v2Client.delete(`/doc/articles/${id}/file`),

  // ─── 관련자료(Related) ────────────────────────────────────

  /** 관련자료 목록 조회 */
  getRelatedList: (params) => v2Client.get('/doc/related', { params }),

  /** 관련자료 상세 조회 */
  getRelatedDetail: (id) => v2Client.get(`/doc/related/${id}`),

  /** 관련자료 등록 */
  createRelated: (data) => v2Client.post('/doc/related', data),

  /** 관련자료 수정 */
  updateRelated: (id, data) => v2Client.put(`/doc/related/${id}`, data),

  /** 관련자료 삭제 */
  removeRelated: (id) => v2Client.delete(`/doc/related/${id}`),
};

// ═══════════════════════════════════════════════════════════
// 6. homeApi - 홈화면
// ═══════════════════════════════════════════════════════════

export const homeApi = {
  /** 홈 검색 */
  search: (params) => v2Client.get('/home/search', { params }),

  /** 최근 데이터 조회 */
  getRecent: (params) => v2Client.get('/home/recent', { params }),

  /** 통계 조회 */
  getStats: () => v2Client.get('/home/stats'),

  /** 최근 검색어 조회 */
  getRecentSearches: () => v2Client.get('/home/recent-searches'),

  /** 인기 검색어 조회 */
  getPopularSearches: () => v2Client.get('/home/popular-searches'),

  /** 최근 게시물 조회 */
  getRecentArticles: (params) => v2Client.get('/home/recent-articles', { params }),

  /** 활성 팝업 조회 */
  getActivePopups: () => v2Client.get('/home/popups'),
};

// ═══════════════════════════════════════════════════════════
// 7. searchApi - 통합검색
// ═══════════════════════════════════════════════════════════

export const searchApi = {
  /** 통합검색 */
  unifiedSearch: (params) => v2Client.get('/search', { params }),

  /** 자동완성 */
  autocomplete: (params) => v2Client.get('/search/autocomplete', { params }),
};

// ═══════════════════════════════════════════════════════════
// 8. adminApi - 관리자
// ═══════════════════════════════════════════════════════════

export const adminApi = {
  // ─── 대시보드(Dashboard) ──────────────────────────────────

  /** 대시보드 요약 조회 */
  getSummary: () => v2Client.get('/admin/dashboard/summary'),

  /** 방문자 추이 조회 */
  getVisitorTrends: (params) => v2Client.get('/admin/dashboard/visitor-trends', { params }),

  /** 데이터 현황 조회 */
  getDataStatus: () => v2Client.get('/admin/dashboard/data-status'),

  /** 사용자 통계 조회 */
  getUserStats: () => v2Client.get('/admin/dashboard/user-stats'),

  /** API 사용량 조회 */
  getApiUsage: (params) => v2Client.get('/admin/dashboard/api-usage', { params }),

  /** 대시보드 품질 점수 조회 */
  getDashboardQualityScore: () => v2Client.get('/admin/dashboard/quality-score'),

  /** 대시보드 보안 이벤트 조회 */
  getDashboardSecurityEvents: (params) => v2Client.get('/admin/dashboard/security-events', { params }),

  // ─── 사용자 관리(Users) ───────────────────────────────────

  /** 사용자 목록 조회 */
  getUserList: (params) => v2Client.get('/admin/users', { params }),

  /** 사용자 상세 조회 */
  getUserDetail: (id) => v2Client.get(`/admin/users/${id}`),

  /** 사용자 등록 */
  createUser: (data) => v2Client.post('/admin/users', data),

  /** 사용자 수정 */
  updateUser: (id, data) => v2Client.put(`/admin/users/${id}`, data),

  /** 사용자 삭제 */
  removeUser: (id) => v2Client.delete(`/admin/users/${id}`),

  /** 시도 목록 조회 (사용자 관리용) */
  getProvinces: () => v2Client.get('/admin/users/lookups/provinces'),

  /** 권한 템플릿 목록 조회 (사용자 관리용) */
  getAuthTemplates: () => v2Client.get('/admin/users/lookups/auth-templates'),

  /** 비밀번호 변경 URL 생성 */
  createPasswordChangeUrl: (id) => v2Client.post(`/admin/users/${id}/password-change`),

  /** 관리자 비밀번호 변경 (다른 사용자의 비밀번호 변경) */
  changePassword: (data) => v2Client.post('/admin/users/change-password', data),

  /** 계정 잠금 해제 (5회 실패로 잠긴 사용자 해제) */
  unlockUser: (userId) => v2Client.post(`/admin/users/${userId}/unlock`),

  // ─── 권한 템플릿(Auth Templates) ──────────────────────────

  /** 권한 템플릿 목록 조회 */
  getAuthTemplateList: (params) => v2Client.get('/admin/auth-templates', { params }),

  /** 권한 템플릿 상세 조회 */
  getAuthTemplateDetail: (id) => v2Client.get(`/admin/auth-templates/${id}`),

  /** 권한 템플릿 등록 */
  createAuthTemplate: (data) => v2Client.post('/admin/auth-templates', data),

  /** 권한 템플릿 수정 */
  updateAuthTemplate: (id, data) => v2Client.put(`/admin/auth-templates/${id}`, data),

  /** 권한 템플릿 삭제 */
  removeAuthTemplate: (id) => v2Client.delete(`/admin/auth-templates/${id}`),

  // ─── 방문자(Visitors) ─────────────────────────────────────

  /** 방문자 목록 조회 */
  getVisitorList: (params) => v2Client.get('/admin/visitors', { params }),
  getVisitorSummary: () => v2Client.get('/admin/visitors/summary'),
  getVisitorTrendsByDate: (params) => v2Client.get('/admin/visitors/trends', { params }),

  // ─── 감사 로그(Audit) ─────────────────────────────────────

  /** 감사 로그 목록 조회 */
  getAuditLogs: (params) => v2Client.get('/admin/audit', { params }),

  /** 감사 로그 내보내기 (blob) */
  exportAuditLogs: (params) =>
    v2Client.get('/admin/audit/export', {
      params,
      responseType: 'blob',
    }),

  // ─── 워크플로우(Workflow) ──────────────────────────────────

  /** 워크플로우 목록 조회 */
  getWorkflows: (params) => v2Client.get('/admin/workflows', { params }),

  /** 대기 중 워크플로우 조회 */
  getPendingWorkflows: (params) => v2Client.get('/admin/workflows/pending', { params }),

  /** 워크플로우 단계 조회 */
  getWorkflowSteps: (id) => v2Client.get(`/admin/workflows/${id}/steps`),

  /** 워크플로우 승인 */
  approveWorkflow: (id, data) => v2Client.post(`/admin/workflows/${id}/approve`, data),

  /** 워크플로우 반려 */
  rejectWorkflow: (id, data) => v2Client.post(`/admin/workflows/${id}/reject`, data),

  // ─── 알림(Notifications) ──────────────────────────────────

  /** 알림 목록 조회 */
  getNotifications: (params) => v2Client.get('/admin/notifications', { params }),

  /** 알림 읽음 처리 */
  markNotificationRead: (id) => v2Client.put(`/admin/notifications/${id}/read`),

  /** 읽지 않은 알림 수 조회 */
  getUnreadNotificationCount: () => v2Client.get('/admin/notifications/unread-count'),

  /** 알림 설정 조회 */
  getNotificationSettings: () => v2Client.get('/admin/notifications/settings'),

  /** 알림 설정 수정 */
  updateNotificationSettings: (data) => v2Client.put('/admin/notifications/settings', data),

  // ─── 배치 작업(Batch) ─────────────────────────────────────

  /** 배치 작업 목록 조회 */
  getBatchJobs: (params) => v2Client.get('/admin/batch', { params }),

  /** 배치 작업 등록 */
  createBatchJob: (data) => v2Client.post('/admin/batch', data),

  /** 배치 항목 조회 */
  getBatchItems: (id, params) => v2Client.get(`/admin/batch/${id}/items`, { params }),

  /** 배치 작업 재시도 */
  retryBatchJob: (id) => v2Client.post(`/admin/batch/${id}/retry`),

  // ─── 품질 관리(Quality) ───────────────────────────────────

  /** 품질 규칙 목록 조회 */
  getQualityRules: (params) => v2Client.get('/admin/quality/rules', { params }),

  /** 품질 검사 결과 조회 */
  getQualityResults: (params) => v2Client.get('/admin/quality/results', { params }),

  /** 품질 검사 실행 */
  runQualityCheck: (data) => v2Client.post('/admin/quality/check', data),

  /** 품질 보고서 조회 */
  getQualityReport: (params) => v2Client.get('/admin/quality/report', { params }),

  // ─── 팝업 관리(Popups) ────────────────────────────────────

  /** 팝업 목록 조회 */
  getPopupList: (params) => v2Client.get('/admin/popups', { params }),

  /** 팝업 상세 조회 */
  getPopupDetail: (id) => v2Client.get(`/admin/popups/${id}`),

  /** 팝업 등록 */
  createPopup: (data) => v2Client.post('/admin/popups', data),

  /** 팝업 수정 */
  updatePopup: (id, data) => v2Client.put(`/admin/popups/${id}`, data),

  /** 팝업 삭제 */
  deletePopup: (id) => v2Client.delete(`/admin/popups/${id}`),
};

// ═══════════════════════════════════════════════════════════
// 9. commonApi - 공통 코드
// ═══════════════════════════════════════════════════════════

export const commonApi = {
  /** 공통코드 전체 조회 */
  getCodes: (params) => v2Client.get('/common/codes', { params }),

  /** 공통코드 그룹별 조회 */
  getCodesByGroup: (group) => v2Client.get(`/common/codes/${group}`),

  /** 국가 목록 조회 */
  getCountries: (params) => v2Client.get('/common/countries', { params }),
};

// ═══════════════════════════════════════════════════════════
// 9-2. adminMasterApi - 관리자 마스터 데이터 (admin 전용)
// ═══════════════════════════════════════════════════════════
// 백엔드 isAdmin 미들웨어로 보호된 엔드포인트. 프론트는 AdminGuard + NotFound으로 이중 방어.

export const adminMasterApi = {
  // ── 국가 (Country) ──────────────────────────────
  getCountryList: () => v2Client.get('/admin/master/countries'),
  getCountryDetail: (code) => v2Client.get(`/admin/master/countries/${code}`),
  createCountry: (data) => v2Client.post('/admin/master/countries', data),
  updateCountry: (code, data) => v2Client.put(`/admin/master/countries/${code}`, data),
  removeCountry: (code) => v2Client.delete(`/admin/master/countries/${code}`),

  // ── 소속 (Province) ─────────────────────────────
  getProvinceList: () => v2Client.get('/admin/master/provinces'),
  getProvinceDetail: (num) => v2Client.get(`/admin/master/provinces/${num}`),
  createProvince: (data) => v2Client.post('/admin/master/provinces', data),
  updateProvince: (num, data) => v2Client.put(`/admin/master/provinces/${num}`, data),
  removeProvince: (num) => v2Client.delete(`/admin/master/provinces/${num}`),

  // ── 식품 분류 (Food Classification) ──────────────
  // type: 'prd-class-large' | 'prd-class-middle' | 'prd-class-small' | 'vd-class-large' | ...
  getClassList: (type, params) => v2Client.get(`/admin/master/${type}`, { params }),
  createClass: (type, data) => v2Client.post(`/admin/master/${type}`, data),
  // 대분류: key1=lCode, 중분류: key1=lCode, key2=mCode, 소분류: +key3=sCode
  updateClass: (type, keys, data) => {
    const pathKeys = [keys.classLCode, keys.classMCode, keys.classSCode].filter(Boolean).join('/');
    return v2Client.put(`/admin/master/${type}/${pathKeys}`, data);
  },
  removeClass: (type, keys) => {
    const pathKeys = [keys.classLCode, keys.classMCode, keys.classSCode].filter(Boolean).join('/');
    return v2Client.delete(`/admin/master/${type}/${pathKeys}`);
  },

  // ── 시스템 유지보수 이력 (System Maintenance) ─────
  getSystemMaintenanceList: () => v2Client.get('/admin/master/system-maintenance'),
  getSystemMaintenanceDetail: (idx) => v2Client.get(`/admin/master/system-maintenance/${idx}`),
  createSystemMaintenance: (data) => v2Client.post('/admin/master/system-maintenance', data),
  updateSystemMaintenance: (idx, data) => v2Client.put(`/admin/master/system-maintenance/${idx}`, data),
  removeSystemMaintenance: (idx) => v2Client.delete(`/admin/master/system-maintenance/${idx}`),
};

// ═══════════════════════════════════════════════════════════
// 10. authApi - 인증 관련
// ═══════════════════════════════════════════════════════════

export const authApi = {
  /**
   * 본인 비밀번호 변경 (로그인 상태, 현재 비밀번호 + 새 비밀번호)
   * 백엔드 라우트: /auth/change-password — v2Client(baseURL=/api/v2) 사용 불가
   */
  selfChangePassword: (data) => authHttp.post('/auth/change-password', data),

  /**
   * 변경 코드 검증 — GET /auth/verify-change-code/:code
   * 응답: { success, data: { userId } }
   */
  verifyChangeCode: (code) => authHttp.get(`/auth/verify-change-code/${code}`),

  /**
   * 코드 기반 비밀번호 변경 — POST /auth/change-password
   * body: { changeCode, hashedPassword }
   */
  changePasswordByCode: (payload) => authHttp.post('/auth/change-password', payload),
};
